/**
 * Enhanced Blockchain Data Manager
 *
 * Implements three-tier caching system for optimal performance and scalability:
 * - Tier 1: Always cached active data (event-driven updates)
 * - Tier 2: On-demand cached historical data (filter-triggered)
 * - Tier 3: Never cached large datasets (always fresh)
 *
 * Key Features:
 * - Smart data fetching based on user intent
 * - User-specific data queries for personalized sections
 * - Event-driven updates for real-time synchronization
 * - RPC call deduplication and throttling
 * - Scalable handling of years of accumulated data
 */

import { createContract } from '../components/EthersSetup.js';
import GameRegistryConfig from '../deployedContractsInfo/GameRegistry.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';

class BlockchainDataManager {
  constructor() {
    // Three-tier caching system
    this.tier1Cache = new Map(); // Always cached: active data
    this.tier2Cache = new Map(); // On-demand: historical data
    this.tier3Queries = new Set(); // Never cached: large datasets

    // Request management
    this.activeQueries = new Map(); // Prevent duplicate RPC calls
    this.pendingRequests = new Map(); // Track ongoing requests

    // Event management
    this.contracts = new Map();
    this.subscribers = new Map();

    // Configuration
    this.config = {
      tier1CacheExpiry: 300000, // 5 minutes for active data
      tier2CacheExpiry: 1800000, // 30 minutes for historical data
      maxCacheSize: 200,
      batchDelay: 100 // ms to batch multiple requests
    };

    // Performance monitoring
    this.stats = {
      rpcCalls: 0,
      cacheHits: 0,
      deduplicationSaves: 0,
      startTime: Date.now()
    };

    // Setup event listeners for polling configuration changes
    this.setupPollingEventListeners();

    console.log('🏗️ Enhanced BlockchainDataManager initialized');
  }

  /**
   * Initialize the manager with blockchain configuration
   */
  async initialize(config) {
    const {
      gameMakerAddress,
      gameMakerAbi,
      gameAddresses = {},
      provider,
      playerAccount
    } = config;

    if (!gameMakerAddress || !gameMakerAbi) {
      throw new Error('Missing required blockchain configuration');
    }

    this.blockchainConfig = config;
    console.log('🔧 Initializing BlockchainDataManager with config:', {
      gameMakerAddress,
      playerAccount,
      gameCount: Object.keys(gameAddresses).length
    });

    try {
      // Create and cache GameMaker contract
      const gameMakerContract = await this.getContract(gameMakerAddress, gameMakerAbi, false);
      this.contracts.set('GameMaker', gameMakerContract);

      // Create and cache GameRegistry contract
      const gameRegistryContract = await this.getContract(
        contractAddresses.GameRegistry,
        GameRegistryConfig.abi,
        false
      );
      this.contracts.set('GameRegistry', gameRegistryContract);

      // Create and cache game contracts for event listening
      await this.setupGameContracts();

      // Initialize Tier 1 cache with active data
      await this.initializeTier1Cache();

      // Setup smart polling system for real-time updates
      await this.setupSmartPolling();

      // Mark as initialized
      this.isInitialized = true;

      console.log('✅ BlockchainDataManager initialized successfully');
      this.notifySubscribers('initialized', { success: true });

    } catch (error) {
      console.error('❌ Failed to initialize BlockchainDataManager:', error);
      throw error;
    }
  }

  /**
   * Initialize Tier 1 cache with always-needed active data
   */
  async initializeTier1Cache() {
    console.log('🔄 Initializing Tier 1 cache with active data...');

    const gameMakerContract = this.contracts.get('GameMaker');
    if (!gameMakerContract) {
      throw new Error('GameMaker contract not available');
    }

    try {
      // Batch initial data load for efficiency
      const [activeOffers, activeGameInstances] = await Promise.all([
        this.performRpcCall('getOffers', gameMakerContract, ['', true]), // Active offers only
        this.performRpcCall('getGameInstances', gameMakerContract, ['', '0x0000000000000000000000000000000000000000', true]) // Active games only
      ]);

      // Cache in Tier 1
      this.tier1Cache.set('activeOffers', {
        data: activeOffers,
        timestamp: Date.now(),
        key: 'activeOffers'
      });

      this.tier1Cache.set('activeGameInstances', {
        data: activeGameInstances,
        timestamp: Date.now(),
        key: 'activeGameInstances'
      });

      console.log('✅ Tier 1 cache initialized:', {
        activeOffers: activeOffers.length,
        activeGameInstances: activeGameInstances.length
      });

      // Debug: Log some sample data to verify structure
      if (activeGameInstances.length > 0) {
        console.log('📊 Sample game instance:', activeGameInstances[0]);
      } else {
        console.log('⚠️ No active game instances found');
      }

    } catch (error) {
      console.error('❌ Failed to initialize Tier 1 cache:', error);
      throw error;
    }
  }

  /**
   * Get or create contract instance with caching
   */
  async getContract(address, abi, needsSigner = false) {
    const contractKey = `${address}_${needsSigner}`;

    if (this.contracts.has(contractKey)) {
      return this.contracts.get(contractKey);
    }

    try {
      const contract = await createContract(address, abi, needsSigner);
      if (contract) {
        this.contracts.set(contractKey, contract);
      }
      return contract;
    } catch (error) {
      console.error(`Failed to create contract ${contractKey}:`, error);
      return null;
    }
  }

  /**
   * Get registered game names from GameRegistry contract
   */
  async getRegisteredGameNames() {
    try {
      const gameRegistryContract = this.contracts.get('GameRegistry');
      if (!gameRegistryContract) {
        console.warn('⚠️ GameRegistry contract not available');
        return [];
      }

      const gameNames = await gameRegistryContract.getAllGameNames();
      console.log('🎮 Retrieved game names from GameRegistry:', gameNames);
      return gameNames;
    } catch (error) {
      console.error('🚨 Failed to get registered game names:', error);
      return [];
    }
  }


  /**
   * Setup game contracts for event listening
   */
  async setupGameContracts() {
    console.log('🎮 Setting up game contracts for event listening...');

    // Get game names dynamically from GameRegistry
    const registeredGameNames = await this.getRegisteredGameNames();
    console.log('🎮 Found registered games:', registeredGameNames);

    // Get game addresses from GameRegistry for each game
    const gameRegistryContract = this.contracts.get('GameRegistry');
    const availableGames = [];
    const gameAddressMap = {};

    for (const gameName of registeredGameNames) {
      try {
        const gameInfo = await gameRegistryContract.getGameInfo(gameName);
        if (gameInfo.gameAddress && gameInfo.gameAddress !== '0x0000000000000000000000000000000000000000') {
          availableGames.push(gameName);
          gameAddressMap[gameName] = gameInfo.gameAddress;
          console.log(`🎮 Found ${gameName} at address: ${gameInfo.gameAddress}`);
        } else {
          console.log(`⏭️ Skipping ${gameName} - no valid address`);
        }
      } catch (error) {
        console.warn(`⚠️ Failed to get address for ${gameName}:`, error.message);
      }
    }

    console.log('🎮 Available games with valid addresses:', availableGames);

    for (const gameName of availableGames) {
      const address = gameAddressMap[gameName];

      try {
        // Load the ABI for this game contract
        const gameAbi = await import(`../deployedContractsInfo/${gameName}.json`);
        const abi = gameAbi.default?.abi || gameAbi.abi;

        if (!abi) {
          console.warn(`⚠️ No ABI found for ${gameName}`);
          continue;
        }

        // Create contract instance
        const contract = await this.getContract(address, abi, false);
        if (contract) {
          this.contracts.set(gameName, contract);
          console.log(`✅ Game contract ${gameName} ready for events at ${address}`);
        }
      } catch (error) {
        console.warn(`⚠️ Failed to setup ${gameName} contract:`, error.message);
      }
    }

    console.log(`🎮 Game contract setup complete - ${this.contracts.size - 1} contracts ready`);
  }

  /**
   * Smart data fetching with three-tier strategy
   */
  async getData(dataType, filters = {}) {
    const {
      gameFilter = '',
      activeOnly = true,
      userAddress = null,
      includeInactive = false,
      forceRefresh = false
    } = filters;

    console.log(`🚀 getData called:`, { dataType, filters });

    // Check if manager is initialized for non-user requests
    if (!this.isInitialized && !userAddress) {
      console.log(`⏳ Manager not initialized yet, returning empty data for ${dataType}`);
      return [];
    }

    // Generate cache key for request
    const cacheKey = this.generateCacheKey(dataType, filters);

    // Check if this is a Tier 1 request (active data)
    if (activeOnly && !userAddress) {
      console.log(`📊 Using Tier 1 data path for ${dataType}`);
      return this.getTier1Data(dataType, filters);
    }

    // Check if this is a user-specific request (personalized data)
    if (userAddress) {
      console.log(`👤 Using user-specific data path for ${dataType}`);
      return this.getUserSpecificData(dataType, userAddress, filters);
    }

    // Check if this is a Tier 2 request (historical data)
    if (includeInactive) {
      console.log(`📚 Using Tier 2 data path for ${dataType}`);
      return this.getTier2Data(dataType, filters, forceRefresh);
    }

    // Default to active data
    console.log(`📊 Using default Tier 1 data path for ${dataType}`);
    return this.getTier1Data(dataType, filters);
  }

  /**
   * Get Tier 1 data (always cached active data)
   */
  async getTier1Data(dataType, filters) {
    const cacheKey = dataType === 'offers' ? 'activeOffers' : 'activeGameInstances';
    const cachedData = this.tier1Cache.get(cacheKey);

    console.log(`🔍 getTier1Data called for dataType: ${dataType}, cacheKey: ${cacheKey}`);

    if (cachedData && !this.isCacheExpired(cachedData, this.config.tier1CacheExpiry)) {
      this.stats.cacheHits++;
      const filteredData = this.filterData(cachedData.data, filters);
      console.log(`📦 Tier 1 cache hit for ${cacheKey}: ${cachedData.data.length} raw items, ${filteredData.length} filtered items`);
      return filteredData;
    }

    // Cache miss or expired - refresh from blockchain
    console.log(`🔄 Refreshing Tier 1 cache for ${cacheKey}`);
    await this.refreshTier1Cache(cacheKey);

    const refreshedData = this.tier1Cache.get(cacheKey);
    const result = refreshedData ? this.filterData(refreshedData.data, filters) : [];
    console.log(`📊 Tier 1 data result for ${cacheKey}: ${result.length} items after filtering`);
    return result;
  }

  /**
   * Get user-specific data (personalized queries)
   */
  async getUserSpecificData(dataType, userAddress, filters = {}) {
    // Check if manager is initialized before proceeding
    if (!this.isInitialized) {
      console.log(`⏳ Manager not initialized yet, returning empty data for user ${dataType}`);
      return [];
    }

    const cacheKey = `user_${dataType}_${userAddress}`;
    const cachedData = this.tier2Cache.get(cacheKey);
    const forceRefresh = filters.forceRefresh || false;

    if (!forceRefresh && cachedData && !this.isCacheExpired(cachedData, this.config.tier2CacheExpiry)) {
      this.stats.cacheHits++;
      return this.filterData(cachedData.data, filters);
    }

    // Fetch user-specific data from blockchain
    console.log(`🔄 Fetching user-specific ${dataType} for ${userAddress}${forceRefresh ? ' (FORCE REFRESH)' : ''}`);

    const gameMakerContract = this.contracts.get('GameMaker');
    if (!gameMakerContract) {
      console.log(`⏳ GameMaker contract not ready yet, returning empty data for user ${dataType}`);
      return [];
    }

    // Additional validation for draw offers
    if (dataType === 'drawOffers') {
      if (!gameMakerContract.address) {
        console.error(`🔴 GameMaker contract has no address for drawOffers request`);
        return [];
      }
      console.log(`🎯 Processing drawOffers request with GameMaker contract: ${gameMakerContract.address}`);
    }

    try {
      let userData;

      if (dataType === 'offers') {
        // Get all offers (active + inactive) for this user
        const allOffers = await this.performRpcCall('getOffers', gameMakerContract, ['', false]);
        userData = allOffers.filter(offer =>
          offer.player1?.toLowerCase() === userAddress.toLowerCase() ||
          (offer.targetPlayer && offer.targetPlayer.toLowerCase() === userAddress.toLowerCase())
        );
      } else if (dataType === 'gameInstances') {
        // Get all game instances (active + inactive) for this user
        const allInstances = await this.performRpcCall('getGameInstances', gameMakerContract, ['', userAddress, false]);
        userData = allInstances;
      } else if (dataType === 'drawOffers') {
        // Get draw offers for user's active games
        const userGameInstances = await this.performRpcCall('getGameInstances', gameMakerContract, ['', userAddress, true]);
        userData = [];

        console.log(`📋 Found ${userGameInstances ? userGameInstances.length : 0} active game instances for draw offers`);

        if (userGameInstances && userGameInstances.length > 0) {
          console.log(`🎮 Active game instances:`, userGameInstances.map(inst => ({
            offerID: inst.offerID,
            gameID: inst.gameID,
            accepted: inst.accepted,
            player1: inst.player1,
            player2: inst.player2
          })));
        }

        // For each active game, get draw offers
        for (const instance of userGameInstances || []) {
          try {
            // Validate instance data before making contract call
            if (!instance || instance.offerID === undefined || instance.offerID === null) {
              console.warn(`⚠️ Skipping invalid instance:`, instance);
              continue;
            }

            // For game instances (not offers), they are by definition already accepted
            // Only offers have an 'accepted' property - game instances don't need this check
            console.log(`🎮 Processing active game instance - draw offers are valid for all active games`);

            console.log(`🎮 Processing game instance:`, {
              offerID: instance.offerID,
              gameID: instance.gameID,
              accepted: instance.accepted,
              acceptedType: typeof instance.accepted,
              acceptedValue: instance.accepted,
              gameStatus: instance.gameStatus,
              player1: instance.player1,
              player2: instance.player2,
              currentUser: userAddress
            });

            console.log(`🔍 Fetching draw offers for offer ${instance.offerID}, game ${instance.gameID}`);
            const drawOffersData = await this.performRpcCall('getDrawOffers', gameMakerContract, [instance.offerID]);

            if (drawOffersData && drawOffersData.player1Offer && drawOffersData.player2Offer) {
              if (drawOffersData.player1Offer.active || drawOffersData.player2Offer.active) {
                console.log(`✅ Found active draw offers for offer ${instance.offerID}`);
                userData.push({
                  offerId: instance.offerID,
                  gameId: instance.gameID,
                  player1Offer: drawOffersData.player1Offer,
                  player2Offer: drawOffersData.player2Offer
                });
              } else {
                console.log(`📝 No active draw offers for offer ${instance.offerID}`);
              }
            } else {
              console.warn(`⚠️ Invalid draw offers data for offer ${instance.offerID}:`, drawOffersData);
            }
          } catch (error) {
            console.warn(`⚠️ Failed to get draw offers for offer ${instance.offerID}:`, error.message);
            // Additional debugging for contract issues
            if (error.message.includes('invalid address') || error.message.includes('ENS name')) {
              console.error(`🔴 Contract error for offer ${instance.offerID}. GameMaker contract:`, gameMakerContract?.address);
            }
          }
        }
      } else {
        userData = [];
      }

      // Cache the result
      this.tier2Cache.set(cacheKey, {
        data: userData,
        timestamp: Date.now(),
        key: cacheKey
      });

      return this.filterData(userData, filters);

    } catch (error) {
      console.error(`Failed to fetch user-specific ${dataType}:`, error);
      return [];
    }
  }

  /**
   * Get Tier 2 data (on-demand historical data)
   */
  async getTier2Data(dataType, filters, forceRefresh = false) {
    const cacheKey = `historical_${dataType}_${this.generateFilterKey(filters)}`;
    const cachedData = this.tier2Cache.get(cacheKey);

    if (!forceRefresh && cachedData && !this.isCacheExpired(cachedData, this.config.tier2CacheExpiry)) {
      this.stats.cacheHits++;
      return this.filterData(cachedData.data, filters);
    }

    // Fetch historical data from blockchain
    console.log(`🔄 Fetching historical ${dataType} data`);

    const gameMakerContract = this.contracts.get('GameMaker');
    if (!gameMakerContract) {
      throw new Error('GameMaker contract not available');
    }

    try {
      let historicalData;

      if (dataType === 'offers') {
        historicalData = await this.performRpcCall('getOffers', gameMakerContract, [filters.gameFilter || '', false]);
      } else if (dataType === 'gameInstances') {
        historicalData = await this.performRpcCall('getGameInstances', gameMakerContract, [
          filters.gameFilter || '',
          '0x0000000000000000000000000000000000000000',
          false
        ]);
      } else {
        historicalData = [];
      }

      // Cache the result
      this.tier2Cache.set(cacheKey, {
        data: historicalData,
        timestamp: Date.now(),
        key: cacheKey
      });

      return this.filterData(historicalData, filters);

    } catch (error) {
      console.error(`Failed to fetch historical ${dataType}:`, error);
      return [];
    }
  }

  /**
   * Setup smart configurable polling system
   */
  async setupSmartPolling() {
    console.log('🔄 Setting up smart configurable polling system...');

    try {
      // Initialize polling configuration
      this.initializePollingConfig();

      // Initialize polled contracts list
      this.initializePolledContracts();

      // Start the polling heartbeat
      this.startPollingHeartbeat();

      console.log('✅ Smart polling system initialized');

    } catch (error) {
      console.error('❌ Failed to setup smart polling system:', error);
    }
  }

  /**
   * Initialize polling configuration from localStorage
   */
  initializePollingConfig() {
    // Get user-configured polling frequency (default: 10 calls per minute)
    const savedFrequency = localStorage.getItem('rpc_polling_frequency');
    this.pollingFrequencyPerMinute = savedFrequency ? parseInt(savedFrequency) : 10;

    // Initialize pause state (temporary, not persisted)
    this.isPaused = false;
    this.pauseReason = '';

    // Calculate interval in milliseconds
    this.pollingIntervalMs = (60 * 1000) / this.pollingFrequencyPerMinute;

    console.log(`🎛️ Polling frequency: ${this.pollingFrequencyPerMinute} calls/minute (every ${this.pollingIntervalMs}ms)`);
  }

  /**
   * Initialize polled contracts list with GameMaker (always included)
   */
  initializePolledContracts() {
    this.polledContracts = ['GameMaker']; // Always include GameMaker
    this.currentPollingIndex = 0;

    console.log(`📋 Initialized polled_contracts:`, this.polledContracts);
  }

  /**
   * Start the main polling heartbeat
   */
  startPollingHeartbeat() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    if (this.pollingIntervalMs === null || this.pollingFrequencyPerMinute === 0) {
      // Manual mode - no automatic polling
      console.log('⏸️ Polling heartbeat stopped (manual mode)');
      return;
    }

    if (this.isPaused) {
      // Paused state - no automatic polling
      const reason = this.pauseReason ? ` (${this.pauseReason})` : '';
      console.log(`⏸️ Polling heartbeat stopped (paused${reason})`);
      return;
    }

    this.pollingTimer = setInterval(() => {
      this.executePoll();
    }, this.pollingIntervalMs);

    console.log(`⏰ Polling heartbeat started (${this.pollingIntervalMs}ms interval)`);
  }

  /**
   * Execute a single poll iteration
   */
  async executePoll() {
    // Safety check: ensure polledContracts is initialized
    if (!this.polledContracts || !Array.isArray(this.polledContracts)) {
      console.warn('⚠️ Polling system not initialized - initializing now');
      this.initializePolledContracts();
    }

    if (this.polledContracts.length === 0) {
      console.warn('⚠️ No contracts in polled_contracts list');
      return;
    }

    // Get the contract to poll (round-robin)
    const contractName = this.polledContracts[this.currentPollingIndex];
    this.currentPollingIndex = (this.currentPollingIndex + 1) % this.polledContracts.length;

    console.log(`🔄 Polling ${contractName} (${this.currentPollingIndex}/${this.polledContracts.length})`);

    try {
      await this.pollContract(contractName);
      this.showVisualFeedback('success');
      console.log(`✅ Successfully polled ${contractName}`);

      // Update polled contracts list based on results (except for GameMaker)
      if (contractName !== 'GameMaker') {
        await this.checkAndCleanupContract(contractName);
      } else {
        // For GameMaker polls, update the contracts list based on user activity
        await this.updatePolledContractsFromActivity();
      }

    } catch (error) {
      this.showVisualFeedback('error');
      console.log(`❌ Failed to poll ${contractName}:`, error.message);
      // Continue with heartbeat - no retry storms
    }
  }

  /**
   * Poll a specific contract for data
   */
  async pollContract(contractName) {
    if (contractName === 'GameMaker') {
      // Refresh offers and game instances from GameMaker
      this.tier1Cache.delete('activeOffers');
      this.tier1Cache.delete('activeGameInstances');
      await this.refreshTier1Cache('activeOffers');
      await this.refreshTier1Cache('activeGameInstances');

      // Also refresh user-specific data
      await this.invalidateAndRefreshTier2CacheByPattern('user_offers');
      await this.invalidateAndRefreshTier2CacheByPattern('user_gameInstances');
      await this.invalidateAndRefreshTier2CacheByPattern('user_drawOffers');

      // Notify subscribers
      this.notifySubscribers('offersUpdated', { source: 'polling' });
      this.notifySubscribers('gameInstancesUpdated', { source: 'polling' });
      this.notifySubscribers('drawOffersUpdated', { source: 'polling' });

    } else {
      // For game contracts, just refresh game instances
      this.tier1Cache.delete('activeGameInstances');
      await this.refreshTier1Cache('activeGameInstances');
      await this.invalidateAndRefreshTier2CacheByPattern('user_gameInstances');

      // Notify subscribers
      this.notifySubscribers('gameInstancesUpdated', { source: 'polling', contract: contractName });
    }
  }

  /**
   * Setup event listeners for polling configuration changes
   */
  setupPollingEventListeners() {
    if (typeof window !== 'undefined') {
      // Listen for polling frequency changes from UI
      window.addEventListener('pollingFrequencyChanged', (event) => {
        const newFrequency = event.detail.frequency;
        console.log(`🎛️ Polling frequency changed to: ${newFrequency} calls/minute`);

        // Update configuration
        this.pollingFrequencyPerMinute = newFrequency;

        // Recalculate polling based on new frequency and current pause state
        this.recalculatePolling();
      });

      // Listen for manual poll requests
      window.addEventListener('manualPollRequested', () => {
        console.log('🔄 Manual poll requested');
        this.executePoll();
      });

      // Listen for pause state changes from UI
      window.addEventListener('pollingPauseChanged', (event) => {
        this.isPaused = event.detail.isPaused;
        this.pauseReason = event.detail.reason;
        const reason = this.pauseReason ? ` (${this.pauseReason})` : '';
        console.log(`⏸️ Polling ${this.isPaused ? 'paused' : 'resumed'}${reason}`);
        this.recalculatePolling();
      });

      // Listen for auto-pause requests from game pages
      window.addEventListener('setPollingPause', (event) => {
        this.isPaused = event.detail.isPaused;
        this.pauseReason = event.detail.reason;
        this.recalculatePolling();

        // Notify PollingConfig UI of the change
        window.dispatchEvent(new CustomEvent('pollingPauseChanged', {
          detail: { isPaused: this.isPaused, reason: this.pauseReason }
        }));
      });
    }
  }

  /**
   * Recalculate polling state based on frequency and pause settings
   */
  recalculatePolling() {
    // Stop existing polling
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    // Don't start if paused OR frequency is 0
    if (this.isPaused || this.pollingFrequencyPerMinute === 0) {
      let reason = 'unknown';
      if (this.isPaused) {
        reason = `paused (${this.pauseReason})`;
      } else if (this.pollingFrequencyPerMinute === 0) {
        reason = 'manual mode';
      }
      console.log(`⏸️ Polling stopped: ${reason}`);
      return;
    }

    // Start normal polling
    this.pollingIntervalMs = (60 * 1000) / this.pollingFrequencyPerMinute;
    this.pollingTimer = setInterval(() => this.executePoll(), this.pollingIntervalMs);
    console.log(`⏰ Polling resumed: ${this.pollingFrequencyPerMinute}/min (every ${this.pollingIntervalMs}ms)`);
  }

  /**
   * Show visual feedback for poll success/failure
   */
  showVisualFeedback(type) {
    // Emit event for UI components to show visual feedback
    console.log(`🎨 Emitting visual feedback: ${type}`);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('blockchainPollStatus', {
        detail: { status: type, timestamp: Date.now() }
      }));
    }
  }

  /**
   * Update polled contracts list based on user activity (called after GameMaker polls)
   */
  async updatePolledContractsFromActivity() {
    if (!this.blockchainConfig || !this.blockchainConfig.playerAccount) {
      return;
    }

    const userAddress = this.blockchainConfig.playerAccount;
    const contractsToAdd = new Set();

    // Check user's active offers
    const activeOffers = this.tier1Cache.get('activeOffers')?.data || [];
    for (const offer of activeOffers) {
      if (offer.player1.toLowerCase() === userAddress.toLowerCase() && !offer.accepted) {
        contractsToAdd.add(this.getContractNameForGame(offer.gameName));
      }
    }

    // Check user's active games
    const activeGameInstances = this.tier1Cache.get('activeGameInstances')?.data || [];
    for (const game of activeGameInstances) {
      if ((game.player1.toLowerCase() === userAddress.toLowerCase() ||
           game.player2.toLowerCase() === userAddress.toLowerCase()) &&
          game.gameActive) {
        contractsToAdd.add(this.getContractNameForGame(game.gameName));
      }
    }

    // Add new contracts to polled list
    for (const contractName of contractsToAdd) {
      if (contractName && !this.polledContracts.includes(contractName)) {
        this.polledContracts.push(contractName);
        console.log(`📋 Added ${contractName} to polled_contracts (user has activity)`);
      }
    }
  }

  /**
   * Check if a contract should be removed from polling (called after non-GameMaker polls)
   */
  async checkAndCleanupContract(contractName) {
    if (!this.blockchainConfig || !this.blockchainConfig.playerAccount) {
      return;
    }

    const userAddress = this.blockchainConfig.playerAccount;
    let userHasActivity = false;

    // Check if user has active games for this contract
    const activeGameInstances = this.tier1Cache.get('activeGameInstances')?.data || [];
    for (const game of activeGameInstances) {
      if (this.getContractNameForGame(game.gameName) === contractName &&
          (game.player1.toLowerCase() === userAddress.toLowerCase() ||
           game.player2.toLowerCase() === userAddress.toLowerCase()) &&
          game.gameActive) {
        userHasActivity = true;
        break;
      }
    }

    // Remove contract if no user activity
    if (!userHasActivity) {
      this.removeFromPolledContracts(contractName);
    }
  }

  /**
   * Add a contract to the polled contracts list immediately
   */
  addToPolledContracts(contractName) {
    if (!contractName || this.polledContracts.includes(contractName)) {
      return;
    }

    this.polledContracts.push(contractName);
    console.log(`📋 Added ${contractName} to polled_contracts (immediate add)`);
  }

  /**
   * Notify the manager that a transaction involving a specific game contract is being made
   * This ensures the contract is immediately added to polling for real-time updates
   */
  notifyTransactionInProgress(gameName) {
    if (!gameName) {
      console.warn('⚠️ notifyTransactionInProgress called without gameName');
      return;
    }

    // Normalize game name (handle both "TicTacToe" and "tictactoe" formats)
    const contractName = this.normalizeGameName(gameName);

    console.log(`🔄 Transaction in progress for ${contractName}, adding to polling immediately`);
    this.addToPolledContracts(contractName);
  }

  /**
   * Normalize game name to match contract naming conventions
   */
  normalizeGameName(gameName) {
    // Handle common game name variations
    const nameMap = {
      'tictactoe': 'TicTacToe',
      'TicTacToe': 'TicTacToe',
      'rockpaperscissors': 'RockPaperScissors',
      'RockPaperScissors': 'RockPaperScissors',
      'coinflip': 'CoinFlip',
      'CoinFlip': 'CoinFlip',
      'risk': 'RiskClassic',
      'RiskClassic': 'RiskClassic',
      'mill': 'Mill',
      'Mill': 'Mill'
    };

    return nameMap[gameName] || gameName;
  }

  /**
   * Remove a contract from the polled contracts list
   */
  removeFromPolledContracts(contractName) {
    if (contractName === 'GameMaker') {
      return; // Never remove GameMaker
    }

    const index = this.polledContracts.indexOf(contractName);
    if (index > -1) {
      this.polledContracts.splice(index, 1);
      console.log(`🗑️ Removed ${contractName} from polled_contracts (no user activity)`);

      // Adjust polling index if necessary
      if (this.currentPollingIndex >= this.polledContracts.length) {
        this.currentPollingIndex = 0;
      }
    }
  }

  /**
   * Get contract name for a given game name
   */
  getContractNameForGame(gameName) {
    // Map game names to contract names
    const gameToContractMap = {
      'TicTacToe': 'TicTacToe',
      'RockPaperScissors': 'RockPaperScissors',
      'CoinFlip': 'CoinFlip',
      'Risk': 'Risk',
      'Risk Simple Grid': 'Risk',
      'Mill': 'Mill',
      'DICY': 'DICY',
      'OracleBet': 'OracleBet'
    };

    return gameToContractMap[gameName] || null;
  }

  /**
   * Update polling frequency and restart heartbeat
   */
  updatePollingFrequency(frequencyPerMinute) {
    this.pollingFrequencyPerMinute = frequencyPerMinute;
    this.pollingIntervalMs = (60 * 1000) / frequencyPerMinute;

    // Save to localStorage
    localStorage.setItem('rpc_polling_frequency', frequencyPerMinute.toString());

    // Restart heartbeat with new frequency
    this.startPollingHeartbeat();

    console.log(`🎛️ Updated polling frequency: ${frequencyPerMinute} calls/minute (every ${this.pollingIntervalMs}ms)`);
  }

  /**
   * Collect all contract addresses for the universal filter
   */
  collectAllContractAddresses() {
    const addresses = [];

    // Add all cached contract addresses, but skip internal cache entries
    for (const [contractName, contract] of this.contracts.entries()) {
      // Skip cache entries that match address_needsSigner pattern (e.g., "0x123..._false")
      if (contractName.match(/^0x[a-fA-F0-9]{40}_(true|false)$/)) {
        console.log(`📍 Skipping cache entry: ${contractName}`);
        continue;
      }

      if (contract && contract.address) {
        addresses.push(contract.address);
        console.log(`📍 Added ${contractName} address: ${contract.address}`);
      }
    }

    // Remove duplicates and return
    const uniqueAddresses = [...new Set(addresses)];
    console.log(`📍 Collected ${uniqueAddresses.length} unique contract addresses for monitoring`);

    return uniqueAddresses;
  }

  /**
   * OBSOLETE: Route events based on contract address - replaced by polling system
   * This method is no longer used but kept for reference
   */
  routeEventByAddress(eventLog) {
    // Handle both raw logs and parsed event logs
    const contractAddress = eventLog.address.toLowerCase();
    const eventName = eventLog.event;
    const args = eventLog.args;

    try {
      // Find which contract this event belongs to (skip cache entries)
      let contractName = null;

      for (const [name, contractInstance] of this.contracts.entries()) {
        // Skip cache entries that match address_needsSigner pattern
        if (name.match(/^0x[a-fA-F0-9]{40}_(true|false)$/)) {
          continue;
        }

        if (contractInstance.address.toLowerCase() === contractAddress) {
          contractName = name;
          break;
        }
      }

      if (!contractName) {
        console.warn(`⚠️ Unknown contract address: ${contractAddress}`);
        return;
      }

      console.log(`📡 Routing ${contractName}.${eventName} event:`, args);

      // Route to appropriate handler based on contract type
      if (contractName === 'GameMaker') {
        this.handleGameMakerEvent(eventName, args);
      } else {
        // All other contracts are game contracts
        this.handleGameContractEvent(contractName, eventName, args, eventLog);
      }

    } catch (error) {
      console.error(`❌ Failed to route event from ${contractAddress}:`, error);
    }
  }

  /**
   * OBSOLETE: Handle GameMaker contract events - replaced by polling system
   */
  handleGameMakerEvent(eventName, args) {
    console.log(`🎯 Handling GameMaker event: ${eventName}`);

    // Route to existing handlers based on event type
    if (['GameOffered', 'GameAccepted', 'GameRetracted'].includes(eventName)) {
      this.handleOfferEvent(eventName, args);
    } else if (['GameTimeout', 'DrawAccepted', 'DrawOffered', 'DrawWithdrawn'].includes(eventName)) {
      this.handleGameEvent(eventName, args);
    } else {
      console.log(`📡 Unhandled GameMaker event: ${eventName}`);
    }
  }

  /**
   * OBSOLETE: Handle individual game contract events - replaced by polling system
   */
  async handleGameContractEvent(contractName, eventName, args, parsedLog) {
    console.log(`🎮 Handling ${contractName}.${eventName} event`);

    try {
      // All game contract events should trigger game instances refresh
      this.tier1Cache.delete('activeGameInstances');
      await this.refreshTier1Cache('activeGameInstances');

      // CRITICAL: Also refresh user-specific game instances for personalized sections
      await this.invalidateAndRefreshTier2CacheByPattern('user_gameInstances');

      // Notify subscribers about game instance updates
      this.notifySubscribers('gameInstancesUpdated', {
        contractName,
        eventName,
        args,
        parsedLog
      });

      console.log(`✅ Completed handling ${contractName}.${eventName} event`);

    } catch (error) {
      console.error(`❌ Error handling ${contractName}.${eventName} event:`, error);
    }

    // Handle specific game events if needed
    this.handleSpecificGameEvent(contractName, eventName, args, parsedLog);
  }

  /**
   * OBSOLETE: Handle contract-specific game events - replaced by polling system
   */
  handleSpecificGameEvent(contractName, eventName, args, parsedLog) {
    // This method can be extended to handle game-specific logic
    // For now, we just log the event
    console.log(`🎲 Specific ${contractName} event: ${eventName}`, args);

    // Example: Handle TicTacToe MoveMade differently than Risk TurnSubmitted
    // if (contractName === 'TicTacToe' && eventName === 'MoveMade') {
    //   // TicTacToe specific logic
    // } else if (contractName === 'Risk' && eventName === 'TurnSubmitted') {
    //   // Risk specific logic
    // }
  }


  /**
   * Handle offer-related events
   */
  async handleOfferEvent(eventName, args) {
    console.log(`🎯 Handling offer event: ${eventName}`, args);

    // Invalidate and refresh active offers cache
    this.tier1Cache.delete('activeOffers');
    await this.refreshTier1Cache('activeOffers');

    // Invalidate and force refresh related Tier 2 caches
    await this.invalidateAndRefreshTier2CacheByPattern('historical_offers');
    await this.invalidateAndRefreshTier2CacheByPattern('user_offers');

    // When an offer is accepted, a new game instance is created
    if (eventName === 'GameAccepted') {
      console.log(`🎮 GameAccepted detected - refreshing game instances`);
      this.tier1Cache.delete('activeGameInstances');
      await this.refreshTier1Cache('activeGameInstances');

      // Also refresh user game instances
      await this.invalidateAndRefreshTier2CacheByPattern('user_gameInstances');

      // Notify game instance subscribers
      this.notifySubscribers('gameInstancesUpdated', { eventName, args });
    }

    console.log(`✅ Completed handling offer event: ${eventName}`);

    // Notify subscribers
    this.notifySubscribers('offersUpdated', { eventName, args });
  }

  /**
   * Handle game-related events
   */
  async handleGameEvent(eventName, args) {
    console.log(`🎯 Handling game event: ${eventName}`, args);

    // Handle draw offer events
    if (eventName === 'DrawOffered' || eventName === 'DrawWithdrawn' || eventName === 'DrawAccepted') {
      // Invalidate draw offers cache
      await this.invalidateAndRefreshTier2CacheByPattern('user_drawOffers');

      // Notify draw offer subscribers
      this.notifySubscribers('drawOffersUpdated', { eventName, args });
    }

    // For all game events, invalidate and refresh game instances cache
    this.tier1Cache.delete('activeGameInstances');
    await this.refreshTier1Cache('activeGameInstances');

    // Invalidate and force refresh related Tier 2 caches
    await this.invalidateAndRefreshTier2CacheByPattern('historical_gameInstances');
    await this.invalidateAndRefreshTier2CacheByPattern('user_gameInstances');

    console.log(`✅ Completed handling game event: ${eventName}`);

    // Notify subscribers
    this.notifySubscribers('gameInstancesUpdated', { eventName, args });
  }

  /**
   * Refresh specific Tier 1 cache entry
   */
  async refreshTier1Cache(cacheKey) {
    const gameMakerContract = this.contracts.get('GameMaker');
    if (!gameMakerContract) return;

    try {
      let data;

      if (cacheKey === 'activeOffers') {
        data = await this.performRpcCall('getOffers', gameMakerContract, ['', true]);
      } else if (cacheKey === 'activeGameInstances') {
        data = await this.performRpcCall('getGameInstances', gameMakerContract, ['', '0x0000000000000000000000000000000000000000', true]);
      }

      if (data) {
        this.tier1Cache.set(cacheKey, {
          data,
          timestamp: Date.now(),
          key: cacheKey
        });
        console.log(`✅ Refreshed ${cacheKey}:`, data.length, 'items');
      }

    } catch (error) {
      console.error(`Failed to refresh ${cacheKey}:`, error);
    }
  }

  /**
   * Perform RPC call with deduplication and monitoring
   */
  async performRpcCall(method, contract, args) {
    const callKey = `${method}_${JSON.stringify(args)}`;

    // Check if this exact call is already in progress
    if (this.activeQueries.has(callKey)) {
      this.stats.deduplicationSaves++;
      return this.activeQueries.get(callKey);
    }

    // Execute the call
    const promise = contract[method](...args);
    this.activeQueries.set(callKey, promise);
    this.stats.rpcCalls++;

    try {
      const result = await promise;
      return result;
    } finally {
      this.activeQueries.delete(callKey);
    }
  }

  /**
   * Filter data based on provided filters
   */
  filterData(data, filters) {
    if (!data || !Array.isArray(data)) return [];

    let filtered = [...data];

    // Apply game filter
    if (filters.gameFilter && filters.gameFilter !== '' && filters.gameFilter !== 'All') {
      filtered = filtered.filter(item => item.gameName === filters.gameFilter);
    }

    // Apply active/inactive filter
    if (filters.activeOnly !== undefined) {
      if (filters.activeOnly) {
        // Show only active items
        filtered = filtered.filter(item => {
          if (item.hasOwnProperty('accepted')) {
            return !item.accepted; // Offers: not accepted
          }
          if (item.hasOwnProperty('status')) {
            const status = parseInt(item.status.toString());
            return status === 0 || status === 1 || status === 6; // Active game states
          }
          return true;
        });
      }
    }

    return filtered;
  }

  /**
   * Utility methods
   */
  generateCacheKey(dataType, filters) {
    const filterKey = this.generateFilterKey(filters);
    return `${dataType}_${filterKey}`;
  }

  generateFilterKey(filters) {
    return Object.keys(filters)
      .sort()
      .map(key => `${key}:${filters[key]}`)
      .join('|');
  }

  isCacheExpired(cacheEntry, maxAge) {
    return Date.now() - cacheEntry.timestamp > maxAge;
  }

  invalidateTier2CacheByPattern(pattern) {
    for (const [key] of this.tier2Cache) {
      if (key.includes(pattern)) {
        this.tier2Cache.delete(key);
      }
    }
  }

  async invalidateAndRefreshTier2CacheByPattern(pattern) {
    console.log(`🧹 Invalidating and refreshing Tier 2 cache pattern: ${pattern}`);

    // First, collect keys that match the pattern and their metadata
    const keysToRefresh = [];
    for (const [key] of this.tier2Cache) {
      if (key.includes(pattern)) {
        // Extract user address and data type from cache key format: "user_{dataType}_{userAddress}"
        const keyParts = key.split('_');
        if (keyParts.length >= 3 && keyParts[0] === 'user') {
          const dataType = keyParts[1];
          const userAddress = keyParts.slice(2).join('_'); // Handle addresses that might contain underscores
          keysToRefresh.push({ key, dataType, userAddress });
        }
      }
    }

    console.log(`📦 Found ${keysToRefresh.length} cache entries to refresh:`, keysToRefresh.map(k => k.key));

    // Invalidate the caches
    this.invalidateTier2CacheByPattern(pattern);

    // Then refresh each affected cache entry
    for (const { key, dataType, userAddress } of keysToRefresh) {
      try {
        console.log(`🔄 Force refreshing ${dataType} cache for user ${userAddress}`);
        await this.getUserSpecificData(dataType, userAddress, { forceRefresh: true });
      } catch (error) {
        console.warn(`⚠️ Failed to refresh cache entry ${key}:`, error.message);
      }
    }

    console.log(`✅ Completed refreshing ${keysToRefresh.length} cache entries for pattern: ${pattern}`);
  }

  /**
   * Subscription management
   */
  subscribe(eventType, callback) {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }
    this.subscribers.get(eventType).add(callback);

    return () => {
      const callbacks = this.subscribers.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.subscribers.delete(eventType);
        }
      }
    };
  }

  notifySubscribers(eventType, data) {
    const callbacks = this.subscribers.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber callback for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Cleanup and destroy
   */
  destroy() {
    // Clear all caches
    this.tier1Cache.clear();
    this.tier2Cache.clear();
    this.activeQueries.clear();

    // Clear polling timer
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }

    // Clear contracts and subscribers
    this.contracts.clear();
    this.subscribers.clear();

    console.log('🧹 BlockchainDataManager destroyed');
  }

  /**
   * Get performance statistics
   */
  getStats() {
    const runtime = Date.now() - this.stats.startTime;
    return {
      ...this.stats,
      runtime,
      cacheHitRate: this.stats.cacheHits / (this.stats.rpcCalls + this.stats.cacheHits),
      tier1CacheSize: this.tier1Cache.size,
      tier2CacheSize: this.tier2Cache.size
    };
  }
}

// Export singleton instance
const blockchainDataManager = new BlockchainDataManager();
export default blockchainDataManager;